import React, { memo } from "react";
import { shallowEqual } from "react-redux";
import Text from "../../../../../../../../shared-components/css-modules/lexus/atoms/Text";
import styles from "./Upholstery.module.scss";
import { useCarConfigDispatch, useCarConfigSelector } from "../../../../../../../redux/store";
import CarDataConsolidator from "../../../../../../../../../shared-logic/utils/classes/CarDataConsolidator";
import Container from "../../../../../../../../shared-components/css-modules/lexus/atoms/Container";
import Button from "../../../../../../../../shared-components/css-modules/lexus/atoms/Button";
import { setUpholstery } from "../../../../../../../redux/slices/CarConfigBasketSlice";
import type { UpholsteryType } from "../../../../../../../../../../common-deprecated/types/CommonTypes";
import { trackUpholsteryAddToBasket } from "../../../../../../../utils/tracking";
import baseSectionStyles from "../../../../../common/section-wrapper/SectionWrapper.module.scss";
import type { SectionWrapperPropagationProps } from "../../../../../common/section-wrapper/SectionWrapper";
import { CarConfigConfigureSectionsLbx } from "../../../../../../../utils/types/SectionTypesLbx";
import SubSectionWrapper from "../SubSectionWrapper";
import { scrollToTarget } from "../../../../../../../../../../common-deprecated/utils/scrollUtils";
import { groupUpholsteriesBaseOnPack } from "../../../../../../../utils/packUtils";
import SpinContainer from "../../../../../common/spin/SpinContainer";
import { setActiveSpin, SpinnerType } from "../../../../../../../redux/slices/CarConfigSpinNavigationSlice";
import { CarConfigLabel, useCarConfigLabel } from "../../../../../../../utils/labelUtils";
import SliderWrapper from "../../../../../common/spin/sliderWrapper/SliderWrapper";
import UpholsteryCard from "../../../../../configure/sections/upholstery/UpholsteryCard";
import { getCarDBAssetUrl } from "../../../../../../../../../shared-logic/utils/imageUtils";

const Upholstery: React.FC<SectionWrapperPropagationProps<CarConfigConfigureSectionsLbx>> = ({
    sectionRefs,
}): JSX.Element => {
    const dispatch = useCarConfigDispatch();
    const commonSettings = useCarConfigSelector((state) => state.commonSettings);

    const upholsteries = useCarConfigSelector(
        (state) =>
            CarDataConsolidator.getUpholsteries(
                state.items.upholsteries,
                state.items.equipmentUpholsteries,
                state.basket,
            ),
        shallowEqual,
    );
    const packs = useCarConfigSelector((state) => state.basket.packs);
    const activeUpholstery = useCarConfigSelector((state) => state.basket.upholstery);
    const activeSpinner = useCarConfigSelector((state) => state.carConfigSpinNavigation.activeSpin);

    const upholsteriesInGroups = groupUpholsteriesBaseOnPack(upholsteries, packs);

    const navigationLabel = useCarConfigLabel("carConfigA11yNavUpholstery");

    return (
        <SubSectionWrapper
            sectionId={CarConfigConfigureSectionsLbx.AtmosphereUpholstery}
            sectionRef={sectionRefs.get(CarConfigConfigureSectionsLbx.AtmosphereUpholstery)}
        >
            <Container>
                <div className={styles.wrapper}>
                    <SpinContainer settings={{ showOnMobile: false, bgColour: "FFFFFF" }} />
                    <SliderWrapper />
                    <div className={styles.configure}>
                        <div className={baseSectionStyles["group-wrapper"]}>
                            <Text
                                tag="h3"
                                variant="apheleia-h4"
                                spacing="spacing-5"
                                className={baseSectionStyles.title}
                            >
                                <CarConfigLabel label="carConfigLbxInteriorColour" />
                            </Text>
                            {Object.entries(upholsteriesInGroups).map(([groupPackId, upholsteriesInGroup]) => {
                                return (
                                    <div
                                        key={groupPackId}
                                        className={
                                            groupPackId !== "standard" ? baseSectionStyles["pack-group-option"] : ""
                                        }
                                    >
                                        {groupPackId !== "standard" && (
                                            <div className={baseSectionStyles["pack-group-option-header"]}>
                                                <CarConfigLabel
                                                    label="carConfigPartOfPack"
                                                    params={{
                                                        pack: packs.find((pack) => pack.shortId === groupPackId)?.name,
                                                    }}
                                                />
                                                <Button
                                                    className={baseSectionStyles["pack-group-button"]}
                                                    variant="btn-text"
                                                    onClick={() => scrollToTarget(CarConfigConfigureSectionsLbx.Packs)}
                                                >
                                                    <CarConfigLabel label="carConfigViewPack" />
                                                </Button>
                                            </div>
                                        )}

                                        <nav aria-label={navigationLabel}>
                                            <div className={styles.upholsteriesMobile}>
                                                <div className={styles.upholsteryItem}>
                                                    <Text
                                                        tag="h2"
                                                        variant="apheleia-h4"
                                                        className={styles.upholsteryItemTitle}
                                                        data-testid="upholstery-name"
                                                    >
                                                        {activeUpholstery.name}
                                                    </Text>
                                                    {activeUpholstery.description && (
                                                        <Text
                                                            tag="span"
                                                            variant="apheleia-body"
                                                            className={styles.upholsteryDescription}
                                                            dangerouslySetInnerHTML={{
                                                                __html: activeUpholstery.description,
                                                            }}
                                                        />
                                                    )}
                                                    <div className={styles.upholsteryItemStatus}>
                                                        <CarConfigLabel label="carConfigIncluded" />
                                                    </div>
                                                </div>
                                                {upholsteriesInGroup.map((upholstery) => (
                                                    <button
                                                        type="button"
                                                        key={upholstery.id}
                                                        aria-pressed={activeUpholstery.id === upholstery.id}
                                                        className={styles.upholsteryItemTile}
                                                        onClick={() => {
                                                            if (activeSpinner !== SpinnerType.Upholstery) {
                                                                dispatch(setActiveSpin(SpinnerType.Upholstery));
                                                            }
                                                            trackUpholsteryAddToBasket();
                                                            dispatch(
                                                                setUpholstery({
                                                                    upholstery: upholstery as UpholsteryType,
                                                                }),
                                                            );
                                                        }}
                                                        data-testid="upholstery-item"
                                                    >
                                                        <img
                                                            src={getCarDBAssetUrl(
                                                                upholstery.image,
                                                                commonSettings.compressedCcisPath,
                                                                128,
                                                                128,
                                                            )}
                                                            loading="lazy"
                                                            data-decorative="yes"
                                                            alt=""
                                                            role="presentation"
                                                            aria-hidden="true"
                                                        />
                                                        <span className={styles.upholsteryTileTitle}>
                                                            {upholstery.name}
                                                        </span>
                                                    </button>
                                                ))}
                                            </div>

                                            <div className={styles.upholsteriesDesktop}>
                                                {upholsteriesInGroup.map((upholstery, index) => (
                                                    <UpholsteryCard
                                                        upholstery={upholstery}
                                                        active={upholstery.id === activeUpholstery.id}
                                                        key={upholstery.id}
                                                        style={{ "--or-animation-delay": `${(index + 1) * 100 + 4}` }}
                                                        onClick={() => {
                                                            dispatch(setActiveSpin(SpinnerType.Upholstery));
                                                            trackUpholsteryAddToBasket();
                                                            dispatch(
                                                                setUpholstery({
                                                                    upholstery: upholstery as UpholsteryType,
                                                                }),
                                                            );
                                                        }}
                                                    />
                                                ))}
                                            </div>
                                        </nav>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </Container>
        </SubSectionWrapper>
    );
};

export default memo(Upholstery);
